import React, { Component, createRef } from 'react';

// CSS
import './Students.scss';

// Helper
import { checkCookie } from '../../Helper/Cookies';
import { apiFetch } from '../../Helper/Api';

// Components
import Header from '../../components/Header/Header';
import { EmptyState, TableWait, Chip, StatTile, UserCell, Tip } from '../../components/UI/Feedback';
import { Toolbar, Segmented } from '../../components/UI/Controls';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';

// Properties
const properties = require('../../properties.json');

const EVENT_LABEL = {
    INVALID_SIGNATURE: 'محاولة تحايل على تطبيق سطح المكتب',
    CAPTURE_ATTEMPT_KEYBOARD: 'محاولة تسجيل/تصوير شاشة (اختصار لوحة مفاتيح)',
    CAPTURE_ATTEMPT_PROCESS: 'برنامج تسجيل شاشة معروف كان شغال',
    WATERMARK_TAMPER: 'شال أو خبّى العلامة المائية في الميتنج المباشر',
    DEVICE_LIMIT_EXCEEDED: 'جهاز زيادة عن المسموح حاول يفتح المحتوى',
    RATE_LIMIT_EXCEEDED: 'طلبات تشغيل كتير في وقت قصير',
};

// Each signal says something different about the student: a forged client or
// a screen recorder is theft, a device over the limit or a burst of requests
// is worth watching. The two read differently on the card.
const EVENT_LOOK = {
    INVALID_SIGNATURE: { icon: 'pi pi-shield', tone: 'bad' },
    WATERMARK_TAMPER: { icon: 'pi pi-eye-slash', tone: 'bad' },
    CAPTURE_ATTEMPT_KEYBOARD: { icon: 'pi pi-camera', tone: 'bad' },
    CAPTURE_ATTEMPT_PROCESS: { icon: 'pi pi-desktop', tone: 'bad' },
    DEVICE_LIMIT_EXCEEDED: { icon: 'pi pi-mobile', tone: 'warn' },
    RATE_LIMIT_EXCEEDED: { icon: 'pi pi-sync', tone: 'warn' },
};

/** How long ago, in the words the teacher would use. */
const since = (value) => {
    if (!value) return '';
    const t = new Date(value);
    if (isNaN(t.getTime())) return '';

    const mins = Math.round((Date.now() - t.getTime()) / 60000);
    if (mins < 1) return 'دلوقتي';
    if (mins < 60) return `من ${mins} دقيقة`;

    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `من ${hrs} ساعة`;

    const days = Math.round(hrs / 24);
    if (days < 30) return `من ${days} يوم`;

    return t.toLocaleDateString('ar-EG');
};

/** Review queue for content-theft signals detected against this teacher's own students -- see
 *  backend-hiilt-manasa-service's StudentService (strike/block logic) and
 *  frontend-hiilt-manasa-desktop's CaptureDetector/DesktopClientVerifier (what triggers a strike). */
class SecurityFlags extends Component {
    constructor(props) {
        super(props);
        this.toast = createRef(null);
    }

    state = {
        data: [],

        // ---- the page's own states: nothing here is ever a blank wait ----
        tableLoading: true,
        globalFilter: null,
        only: null, // "blocked" | "watch" | null -- a view of the rows already loaded
        unblocking: null, // which student's unblock is in flight
    };

    getFlags = () => {
        this.setState({ tableLoading: true });
        apiFetch('/students/security-flags')
            .then((data) => this.setState({ data, tableLoading: false }))
            .catch((error) => {
                this.setState({ tableLoading: false });
                console.error('Error:', error);
            });
    };

    componentDidMount() {
        document.title = properties.site_name + ' | تنبيهات الأمان';
        if (!checkCookie('name')) {
            window.localStorage.removeItem('currentTeacher');
            window.location.href = `/`;
        }
        this.getFlags();
    }

    unblock = (studentProfileId) => {
        this.setState({ unblocking: studentProfileId });
        apiFetch(`/students/${studentProfileId}/unblock`, { method: 'POST' })
            .then(() => {
                this.setState({ unblocking: null });
                this.toast.current.show({ severity: 'success', summary: 'تم', detail: 'تم فك الحظر عن الطالب' });
                this.getFlags();
            })
            .catch((error) => {
                console.error('Error:', error);
                this.setState({ unblocking: null });
                this.toast.current.show({ severity: 'error', summary: 'تعذر', detail: 'حصل خطأ، حاول تاني' });
            });
    };

    render() {
        const { data, tableLoading, only, unblocking } = this.state;

        const blocked = data.filter((r) => r.blocked).length;
        const captures = data.reduce((sum, r) => sum + Number(r.captureAttemptCount || 0), 0);

        // the same rows, seen one group at a time -- nothing is fetched again
        const rows =
            only === 'blocked'
                ? data.filter((r) => r.blocked)
                : only === 'watch'
                ? data.filter((r) => !r.blocked)
                : data;

        // the student, with when their last signal came in underneath
        const nameBody = (row) => {
            const last = (row.recentEvents || [])[0];
            return (
                <UserCell
                    name={row.fullName}
                    sub={last ? `آخر إشارة ${since(last.creationDate)}` : 'مفيش إشارات جديدة'}
                />
            );
        };

        const statusBody = (row) =>
            row.blocked ? <Chip kind="bad">محظور</Chip> : <Chip kind="wait">تحت المراقبة</Chip>;

        // the three counts the row carries, as one reading instead of three
        // columns of bare numbers
        const countsBody = (row) => (
            <span className="ui-bits">
                <Tip text="مخالفات تحايل">
                    <span className={Number(row.securityStrikeCount) ? 'bit is-bad' : 'bit'}>
                        <i className="pi pi-shield"></i>
                        {row.securityStrikeCount || 0}
                    </span>
                </Tip>
                <Tip text="محاولات تسجيل شاشة">
                    <span className={Number(row.captureAttemptCount) ? 'bit is-bad' : 'bit'}>
                        <i className="pi pi-camera"></i>
                        {row.captureAttemptCount || 0}
                    </span>
                </Tip>
                <Tip text="مرات الحظر (تاريخي)">
                    <span className="bit">
                        <i className="pi pi-lock"></i>
                        {row.blockCount || 0}
                    </span>
                </Tip>
            </span>
        );

        // what actually happened, newest first, each one named and dated
        const eventsBody = (row) => {
            const events = row.recentEvents || [];
            if (!events.length) return <span className="ui-bits is-none">مفيش أحداث مسجّلة</span>;

            return (
                <ul className="ui-events">
                    {events.slice(0, 3).map((e, i) => {
                        const look = EVENT_LOOK[e.eventType] || { icon: 'pi pi-info-circle', tone: 'warn' };
                        return (
                            <li className={`is-${look.tone}`} key={i}>
                                <i className={look.icon}></i>
                                <span className="tx">
                                    <b>{EVENT_LABEL[e.eventType] || e.eventType}</b>
                                    <small>
                                        {since(e.creationDate)}
                                        {e.detail ? ` · ${e.detail}` : ''}
                                    </small>
                                </span>
                            </li>
                        );
                    })}
                    {events.length > 3 ? <li className="is-more">+{events.length - 3} أحداث أقدم</li> : null}
                </ul>
            );
        };

        const actionBody = (row) =>
            row.blocked ? (
                <Button
                    label={unblocking === row.studentProfileId ? 'بنفك الحظر…' : 'فك الحظر'}
                    icon={unblocking === row.studentProfileId ? 'pi pi-spin pi-spinner' : 'pi pi-lock-open'}
                    className="p-button-success"
                    disabled={unblocking === row.studentProfileId}
                    onClick={() => this.unblock(row.studentProfileId)}
                />
            ) : (
                <span className="ui-bits is-none">مفيش إجراء مطلوب</span>
            );

        // three states, three tabs, right on the bar -- no drawer in front
        // of them
        const tools = (
            <Segmented
                value={only || 'all'}
                onChange={(key) => this.setState({ only: key === 'all' ? null : key })}
                items={[
                    { key: 'all', label: 'الكل' },
                    { key: 'blocked', label: 'المحظورين' },
                    { key: 'watch', label: 'تحت المراقبة' },
                ]}
            />
        );

        return (
            <>
                <Header pageName="تنبيهات الأمان" />
                <Toast ref={this.toast}></Toast>
                <div className="content">
                    <div className="container">
                        <div className="card">
                            <Card>
                                {/* the shape of the problem, before any row is read */}
                                <div className="ui-tiles">
                                    <StatTile
                                        brand
                                        icon="pi pi-shield"
                                        label="طلاب تحت المراجعة"
                                        value={tableLoading ? '…' : data.length}
                                    />
                                    <StatTile
                                        icon="pi pi-lock"
                                        tone="red"
                                        label="محظورين دلوقتي"
                                        value={tableLoading ? '…' : blocked}
                                    />
                                    <StatTile
                                        icon="pi pi-camera"
                                        tone="gold"
                                        label="محاولات تسجيل شاشة"
                                        value={tableLoading ? '…' : captures}
                                    />
                                </div>

                                <Toolbar
                                    value={this.state.globalFilter || ''}
                                    onSearch={(v) => this.setState({ globalFilter: v })}
                                    placeholder="بحث باسم الطالب"
                                    count={tableLoading ? null : `${rows.length} طالب`}
                                    tools={tools}
                                />

                                <DataTable
                                    sortable
                                    globalFilter={this.state.globalFilter}
                                    className="p-datatable-responsive-demo p-datatable-gridlines p-datatable-striped"
                                    value={rows}
                                    paginator
                                    rows={10}
                                    emptyMessage={
                                        tableLoading ? (
                                            <TableWait />
                                        ) : only ? (
                                            <EmptyState
                                                title={only === 'blocked' ? 'مفيش طالب محظور' : 'مفيش طالب تحت المراقبة'}
                                                hint="شيل الفلتر وشوف باقي التنبيهات"
                                                icon="pi pi-shield"
                                                action={{ label: 'اعرض الكل', onClick: () => this.setState({ only: null }) }}
                                            />
                                        ) : (
                                            <EmptyState
                                                title="مفيش أي تنبيهات"
                                                hint="أي محاولة تسجيل شاشة أو تحايل على تطبيق سطح المكتب هتظهر هنا باسم الطالب"
                                                icon="pi pi-shield"
                                            />
                                        )
                                    }
                                >
                                    <Column sortable field="fullName" header="الطالب" body={nameBody}></Column>
                                    <Column
                                        sortable
                                        field="blocked"
                                        header="الحالة"
                                        body={statusBody}
                                        style={{ width: '150px' }}
                                    ></Column>
                                    <Column
                                        sortable
                                        field="securityStrikeCount"
                                        header="الإشارات"
                                        body={countsBody}
                                        style={{ width: '190px' }}
                                    ></Column>
                                    <Column header="آخر الأحداث" body={eventsBody}></Column>
                                    <Column header="إجراء" body={actionBody} style={{ width: '170px' }}></Column>
                                </DataTable>
                            </Card>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

export default SecurityFlags;
